import { Controller } from "@hotwired/stimulus"

// Hell/Dunkel-Umschalter und Wahl der dunklen Palette. Schreibt das Cookie
// theme (system | light | dark), setzt data-theme bzw. data-dark-palette am
// <html> sofort und verhindert das Absenden des Fallback-Formulars
// (PATCH /theme ohne JavaScript).
//
// - App: Buttons mit data-action="theme#select" data-theme-theme-param="dark"
// - Profil: Radios name="dark_palette" mit data-action="change->theme#palette"
// - Login: ein Button mit data-action="theme#toggle" (hell ↔ dunkel,
//   ausgehend vom gerade sichtbaren Modus)
//
// Mit saveUrl speichert der Controller die Wahl auch am Konto. Mit seenUrl
// meldet er den gezeigten Modus (light oder dark:<palette>), sobald er vom
// zuletzt gemeldeten (seenAppearance) abweicht.
export const THEMES = ["system", "light", "dark"]
export const DARK_PALETTES = ["standard", "dimmed", "graphite", "dusk"]
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export default class extends Controller {
  static targets = ["toggle"]
  static values = { lightLabel: String, darkLabel: String, saveUrl: String, seenUrl: String, seenAppearance: String }

  connect() {
    this.media = window.matchMedia("(prefers-color-scheme: dark)")
    this.onSystemChange = () => this.update()
    this.media.addEventListener("change", this.onSystemChange)
    this.update()
  }

  disconnect() {
    this.media?.removeEventListener("change", this.onSystemChange)
  }

  select(event) {
    event.preventDefault()
    this.apply(event.params.theme)
  }

  toggle(event) {
    event.preventDefault()
    this.apply(this.effectiveTheme === "dark" ? "light" : "dark")
  }

  palette(event) {
    const palette = event.target.value
    if (!DARK_PALETTES.includes(palette)) return

    const root = document.documentElement
    if (palette === "standard") delete root.dataset.darkPalette
    else root.dataset.darkPalette = palette
    this.save({ dark_palette: palette })
    this.update()
  }

  apply(theme) {
    if (!THEMES.includes(theme)) return

    document.cookie = `theme=${theme}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
    const root = document.documentElement
    if (theme === "system") delete root.dataset.theme
    else root.dataset.theme = theme
    this.save({ theme })
    this.update()
  }

  get currentTheme() {
    return document.documentElement.dataset.theme || "system"
  }

  get effectiveTheme() {
    const theme = this.currentTheme
    if (theme !== "system") return theme
    return this.media?.matches ? "dark" : "light"
  }

  // Wie ThemesController#seen: light oder dark:<palette>
  get appearance() {
    if (this.effectiveTheme !== "dark") return "light"
    return `dark:${document.documentElement.dataset.darkPalette || "standard"}`
  }

  update() {
    const current = this.currentTheme
    this.element.querySelectorAll("[data-theme-theme-param]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.themeThemeParam === current))
    })

    if (this.hasToggleTarget) {
      const dark = this.effectiveTheme === "dark"
      this.toggleTarget.setAttribute("aria-pressed", String(dark))
      const label = dark ? this.lightLabelValue : this.darkLabelValue
      if (label) this.toggleTarget.setAttribute("aria-label", label)
    }

    this.reportSeen()
  }

  save(params) {
    if (!this.saveUrlValue) return
    this.saving = this.send(this.saveUrlValue, params)
  }

  reportSeen() {
    if (!this.seenUrlValue) return
    const appearance = this.appearance
    if (appearance === this.seenAppearanceValue) return

    // Erst nach dem Speichern melden: Der Server setzt die Palette des Kontos ein.
    this.seenAppearanceValue = appearance
    const mode = this.effectiveTheme
    Promise.resolve(this.saving).then(() => this.send(this.seenUrlValue, { mode }))
  }

  // Fehler beim Speichern sind nicht kritisch: Cookie und Anzeige stimmen
  // schon, der nächste Seitenaufruf meldet erneut.
  send(url, params) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      method: "PATCH",
      headers: { Accept: "application/json", "Content-Type": "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
      body: JSON.stringify(params),
      credentials: "same-origin",
    }).catch(() => {})
  }
}
